import { eventChannel } from "redux-saga";
import type { ForkEffect } from "redux-saga/effects";
import * as Sentry from "@sentry/react";
import { all, call, cancelled, fork, put, select, take } from "redux-saga/effects";

import {
  completeLocalRecordingPartUpload,
  completeLocalRecordingUpload,
  failedLocalRecordingPartUpload,
  failedLocalRecordingUpload,
  localRecordingPartDoneRecording,
  localRecordingSinglePartInfo,
  localRecordingUploadInfo,
  localRecordingUploadProgress,
  pendingLocalRecordingUpload,
  startLocalRecordingUpload,
} from "../../call/actions";
import { AUTH_USER, INIT_VISITOR_ID, UNAUTH_USER } from "../../../actions/types";
import { sleep } from "../../../../components/utils/util";
import type { LocalMediaStorage as LocalMediaStorageOld } from "../../../../lib/localRecording";
import localMediaRecordingOld from "../../../../lib/localRecording";
import localMediaRecording, { LocalMediaStorage } from "../../../../lib/localRecordingNew";
import type { RootState } from "../../rootReducer";

function createRecorderChannel(recorder: LocalMediaStorage) {
  return eventChannel((emit) => {
    recorder.pendingSyncCallback = (stream) => emit(pendingLocalRecordingUpload(stream));
    recorder.preSyncCallback = (stream) => emit(startLocalRecordingUpload(stream));
    recorder.uploadSuccessCallback = (stream) => emit(completeLocalRecordingUpload(stream));
    recorder.allPartsInfoCallback = (stream, evt) => emit(localRecordingUploadInfo(stream, evt));
    recorder.partInfoCallback = (stream, part, isRecording) =>
      emit(localRecordingSinglePartInfo(stream, part, isRecording));
    recorder.partDoneRecordingCallback = (stream, partId) => emit(localRecordingPartDoneRecording(stream, partId));
    recorder.uploadFailureCallback = (stream, message) => emit(failedLocalRecordingUpload(stream, message));
    recorder.partUploadProgressCallback = (stream, evt) => emit(localRecordingUploadProgress(stream, evt));
    recorder.partUploadFailureCallback = (stream, partId, message) =>
      emit(failedLocalRecordingPartUpload(stream, partId, message));
    recorder.partUploadSuccessCallback = (stream, partId) => emit(completeLocalRecordingPartUpload(stream, partId));

    return () => {
      console.debug("Unsubscribing to media recorder");
      recorder.pendingSyncCallback = () =>
        console.warn("pendingSyncCallback called on an unsubscribed local media recorder");
      recorder.preSyncCallback = () => console.warn("preSyncCallback called on an unsubscribed local media recorder");
      recorder.uploadSuccessCallback = () =>
        console.warn("uploadSuccessCallback called on an unsubscribed local media recorder");
      recorder.uploadFailureCallback = () =>
        console.warn("uploadFailureCallback called on an unsubscribed local media recorder");
      recorder.allPartsInfoCallback = () =>
        console.warn("partsInfoCallback called on an unsubscribed local media recorder");
      recorder.partInfoCallback = () => console.warn("partInfoCallback called on an unsubscribed local media recorder");
      recorder.partDoneRecordingCallback = () =>
        console.warn("partDoneRecordingCallback called on an unsubscribed local media recorder");
      recorder.partUploadProgressCallback = () =>
        console.warn("partUploadProgressCallback called on an unsubscribed local media recorder");
      recorder.partUploadFailureCallback = () =>
        console.warn("partUploadFailureCallback called on an unsubscribed local media recorder");
      recorder.partUploadSuccessCallback = () =>
        console.warn("partUploadSuccessCallback called on an unsubscribed local media recorder");
    };
  });
}

function createRecorderChannelOld(recorder: LocalMediaStorageOld) {
  return eventChannel((emit) => {
    recorder.pendingSyncCallback = (stream) => emit(pendingLocalRecordingUpload(stream));
    recorder.preSyncCallback = (stream) => emit(startLocalRecordingUpload(stream));
    recorder.postSyncCallback = (stream) => emit(completeLocalRecordingUpload(stream));
    recorder.uploadProgressCallback = (stream, evt) =>
      emit(
        localRecordingUploadProgress(stream, {
          partId: "only_part",
          partsUploaded: 0,
          partBytes: evt.totalBytes,
          uploadedBytes: evt.bytesUploaded,
        })
      );

    return () => {
      console.debug("Unsubscribing to media recorder");
      recorder.pendingSyncCallback = () =>
        console.warn("pendingSyncCallback called on an unsubscribed local media recorder");
      recorder.preSyncCallback = () => console.warn("preSyncCallback called on an unsubscribed local media recorder");
      recorder.postSyncCallback = () => console.warn("postSyncCallback called on an unsubscribed local media recorder");
      recorder.uploadProgressCallback = () =>
        console.warn("uploadProgressCallback called on an unsubscribed local media recorder");
    };
  });
}

function* setupLocalMediaRecording(recorder: LocalMediaStorage | LocalMediaStorageOld) {
  // See https://decembersoft.com/posts/redux-saga-put-from-inside-a-callback/ for details on the method I'm using here

  const channel =
    recorder instanceof LocalMediaStorage ? createRecorderChannel(recorder) : createRecorderChannelOld(recorder);

  const taskDispatcher: ForkEffect<any> = yield fork(function* () {
    try {
      yield fork(function* () {
        // sync any local recordings in indexedDB - we attempt this action twice, 60 sec apart
        // so that if the user hits a refresh while a local recording is in progress, the first
        // time when we come back, that doc in IDB won't get picked up but after 60 sec of
        // not being written to, subsequent pass will trigger the upload
        try {
          yield recorder.syncRecordings();
          yield sleep(60_000);
          yield recorder.syncRecordings();
        } catch (e) {
          Sentry.captureException(e);
          console.error(e);
        }
      });

      while (true) {
        const action = yield take(channel);
        yield put(action);
      }
    } catch (e) {
      console.warn(e);
      throw e;
    } finally {
      if (yield cancelled()) {
        // Unsubscribe the recorder
        channel.close();
      }
    }
  });

  return taskDispatcher;
}

export function* initLocalMediaStreaming() {
  while (true) {
    yield take([AUTH_USER, UNAUTH_USER, INIT_VISITOR_ID]);

    const state: RootState = yield select();

    // We sit in the loop until we get a valid visitorId
    if (state.auth.visitorId) {
      break;
    }
  }
  yield all([
    call(setupLocalMediaRecording, localMediaRecordingOld),
    call(setupLocalMediaRecording, localMediaRecording),
  ]);
}
